import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Stack,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import SaveIcon from "@mui/icons-material/Save";
import { useBlocker, useNavigate } from "react-router-dom";

import StockPageHeader from "../../../components/StockPageHeader";
import {
  parsePreparedProductRecipeNotes,
  serializePreparedProductRecipeNotes,
} from "../../elaborados/utils/preparedProductRecipeNotes";
import { createRecipe } from "../api/recipesApi";
import RecipeComponentRow from "../components/RecipeComponentRow";
import RecipeComponentSearch from "../components/RecipeComponentSearch";
import {
  buildRecipePayload,
  componentKey,
  formatRecipeQuantityInput,
  MAX_RECIPE_COMPONENTS_PER_TYPE,
  normalizeRecipeQuantityInput,
  parseRecipeQuantity,
} from "../utils/recipeForm.utils";
import {
  normalizeRecipeSectors,
  RECIPE_SECTORS,
} from "../utils/recipeSector.utils";

const RECIPES_PATH = "/stock/base-de-datos/recetas";

function errorToMessage(error) {
  const message =
    error?.response?.data?.detail ||
    error?.response?.data?.message ||
    error?.data?.message ||
    error?.message;

  return Array.isArray(message)
    ? message.join(" | ")
    : message || "No se pudo crear la receta.";
}

function SectionTitle({ children, helper }) {
  return (
    <Box>
      <Typography sx={{ fontSize: 16, fontWeight: 900, color: "primary.900" }}>
        {children}
      </Typography>
      {helper ? (
        <Typography sx={{ mt: 0.2, fontSize: 11.5, fontWeight: 600, color: "neutral.600" }}>
          {helper}
        </Typography>
      ) : null}
    </Box>
  );
}

function formSignature({ name, sector, isActive, portionQuantity, notes, components }) {
  return JSON.stringify({
    name,
    sector: normalizeRecipeSectors(sector),
    isActive,
    portionQuantity: formatRecipeQuantityInput(portionQuantity),
    notes,
    components: components.map((component) => ({
      kind: component.kind,
      id: component.id,
      quantity: formatRecipeQuantityInput(component.quantity),
      notes: component.notes || "",
    })),
  });
}

export default function CreateRecipePage({
  initialValues,
  pageTitle = "Crear Receta",
  showStatus = false,
  confirmOnCancel = true,
  successMessage = "La receta se creó correctamente.",
  onSave,
  sharedConsumers = [],
}) {
  const navigate = useNavigate();
  const allowNavigationRef = useRef(false);
  const recipeStepKeyRef = useRef(1);
  const initialRecipeNotes = useMemo(
    () => parsePreparedProductRecipeNotes(initialValues?.notes),
    [initialValues?.notes]
  );
  const [name, setName] = useState(initialValues?.name || "");
  const [sector, setSector] = useState(() =>
    normalizeRecipeSectors(initialValues?.sector || ["Cocina"])
  );
  const [isActive, setIsActive] = useState(initialValues?.isActive ?? true);
  const [portionQuantity, setPortionQuantity] = useState(
    formatRecipeQuantityInput(initialValues?.portionQuantity ?? 1)
  );
  const [recipeDescription, setRecipeDescription] = useState(
    initialRecipeNotes.description
  );
  const [recipeSteps, setRecipeSteps] = useState(() =>
    initialRecipeNotes.steps.map((text, index) => ({
      key: `existing-step-${index}`,
      text,
    }))
  );
  const [components, setComponents] = useState(() =>
    (initialValues?.components || []).map((component) => ({
      ...component,
      quantity: formatRecipeQuantityInput(component.quantity),
    }))
  );
  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState("");
  const [saving, setSaving] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);

  const recipeNotes = useMemo(() => {
    const currentSteps = recipeSteps.map((step) => step.text);
    const descriptionUnchanged =
      recipeDescription === initialRecipeNotes.description;
    const stepsUnchanged =
      currentSteps.length === initialRecipeNotes.steps.length &&
      currentSteps.every(
        (step, index) => step === initialRecipeNotes.steps[index]
      );

    if (initialValues?.notes && descriptionUnchanged && stepsUnchanged) {
      return initialValues.notes;
    }
    return serializePreparedProductRecipeNotes(recipeDescription, recipeSteps);
  }, [initialRecipeNotes, initialValues?.notes, recipeDescription, recipeSteps]);

  const currentSignature = useMemo(
    () =>
      formSignature({
        name,
        sector,
        isActive,
        portionQuantity,
        notes: recipeNotes,
        components,
      }),
    [components, isActive, name, portionQuantity, recipeNotes, sector]
  );
  const initialSignatureRef = useRef(currentSignature);
  const dirty = currentSignature !== initialSignatureRef.current;

  const shouldBlockNavigation = useCallback(
    () => confirmOnCancel && dirty && !allowNavigationRef.current,
    [confirmOnCancel, dirty]
  );
  const blocker = useBlocker(shouldBlockNavigation);

  useEffect(() => {
    if (blocker.state === "blocked") setCancelDialogOpen(true);
  }, [blocker.state]);

  useEffect(() => {
    if (!confirmOnCancel || !dirty) return undefined;

    const handleBeforeUnload = (event) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [confirmOnCancel, dirty]);

  const ingredients = useMemo(
    () => components.filter((component) => component.kind === "ingredient"),
    [components]
  );
  const preparedProducts = useMemo(
    () => components.filter((component) => component.kind === "preparedProduct"),
    [components]
  );

  function validate() {
    const nextErrors = {};
    const normalizedName = name.trim();

    if (!normalizedName) nextErrors.name = "Ingresá el nombre de la receta.";
    else if (normalizedName.length > 200) {
      nextErrors.name = "El nombre no puede superar los 200 caracteres.";
    }

    if (!sector.length) nextErrors.sector = "Seleccioná Cocina, Barra o ambos.";
    if (parseRecipeQuantity(portionQuantity) === null) {
      nextErrors.portionQuantity =
        "Ingresá una cantidad positiva con hasta 4 decimales.";
    }
    if (!components.length) {
      nextErrors.components = "Agregá al menos un insumo o elaborado.";
    }
    if ((recipeNotes?.length || 0) > 16000) {
      nextErrors.notes =
        "La descripción y los pasos no pueden superar los 16000 caracteres.";
    }

    const lineErrors = {};
    components.forEach((component) => {
      if (component.isAvailable === false) {
        lineErrors[componentKey(component)] =
          "Este componente está inactivo. Reemplazalo o quitálo.";
      } else if (parseRecipeQuantity(component.quantity) === null) {
        lineErrors[componentKey(component)] =
          "Ingresá una cantidad positiva con hasta 4 decimales.";
      }
    });
    nextErrors.lines = lineErrors;

    setErrors(nextErrors);
    return Object.keys(nextErrors).every(
      (key) => key === "lines" && Object.keys(lineErrors).length === 0
    );
  }

  function handleSelectComponent(option) {
    const typeCount = components.filter(
      (component) => component.kind === option.kind
    ).length;
    if (typeCount >= MAX_RECIPE_COMPONENTS_PER_TYPE) {
      setErrors((current) => ({
        ...current,
        components: `Se alcanzó el máximo de ${MAX_RECIPE_COMPONENTS_PER_TYPE} ${
          option.kind === "ingredient" ? "insumos" : "elaborados"
        } por receta.`,
      }));
      return;
    }

    setComponents((current) => {
      if (current.some((component) => componentKey(component) === componentKey(option))) {
        return current;
      }
      return [...current, { ...option, quantity: "", notes: "" }];
    });
    setErrors((current) => ({ ...current, components: "" }));
  }

  function addRecipeStep() {
    setRecipeSteps((current) => [
      ...current,
      { key: `new-step-${recipeStepKeyRef.current++}`, text: "" },
    ]);
  }

  function updateRecipeStep(index, text) {
    setRecipeSteps((current) =>
      current.map((step, stepIndex) =>
        stepIndex === index ? { ...step, text } : step
      )
    );
    setErrors((current) => ({ ...current, notes: "" }));
  }

  function moveRecipeStep(index, direction) {
    setRecipeSteps((current) => {
      const target = index + direction;
      if (target < 0 || target >= current.length) return current;
      const next = [...current];
      [next[index], next[target]] = [next[target], next[index]];
      return next;
    });
  }

  function removeRecipeStep(index) {
    setRecipeSteps((current) =>
      current.filter((_, stepIndex) => stepIndex !== index)
    );
    setErrors((current) => ({ ...current, notes: "" }));
  }

  function handleChangeComponent(target, changes) {
    const key = componentKey(target);
    setComponents((current) =>
      current.map((component) =>
        componentKey(component) === key ? { ...component, ...changes } : component
      )
    );
    if (Object.prototype.hasOwnProperty.call(changes, "quantity")) {
      setErrors((current) => ({
        ...current,
        lines: { ...current.lines, [key]: "" },
      }));
    }
  }

  function handleRemoveComponent(target) {
    const key = componentKey(target);
    setComponents((current) => current.filter((component) => componentKey(component) !== key));
    setErrors((current) => {
      const nextLines = { ...(current.lines || {}) };
      delete nextLines[key];
      return { ...current, lines: nextLines };
    });
  }

  function requestCancel() {
    if (confirmOnCancel && dirty) {
      setCancelDialogOpen(true);
      return;
    }
    navigate(RECIPES_PATH);
  }

  function keepEditing() {
    if (blocker.state === "blocked") blocker.reset();
    setCancelDialogOpen(false);
  }

  function discardChanges() {
    allowNavigationRef.current = true;
    setCancelDialogOpen(false);
    if (blocker.state === "blocked") blocker.proceed();
    else navigate(RECIPES_PATH);
  }

  async function handleSubmit(event) {
    event.preventDefault();
    if (saving || !validate()) return;

    setSaving(true);
    setSubmitError("");
    try {
      const payload = buildRecipePayload({
        name,
        sector,
        isActive,
        portionQuantity,
        notes: recipeNotes,
        components,
      });
      if (onSave) await onSave(payload);
      else await createRecipe(payload);
      allowNavigationRef.current = true;
      navigate(RECIPES_PATH, {
        replace: true,
        state: { successMessage },
      });
    } catch (error) {
      setSubmitError(errorToMessage(error));
    } finally {
      setSaving(false);
    }
  }

  return (
    <Stack
      component="form"
      onSubmit={handleSubmit}
      noValidate
      spacing={2}
      sx={{
        p: 2,
        flex: 1,
        height: "100%",
        minHeight: 0,
        overflow: "hidden",
        "@media (max-height: 700px)": { p: 1.25, gap: 1 },
      }}
    >
      <StockPageHeader title={pageTitle} onBack={requestCancel} />

      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflow: "hidden",
          bgcolor: "transparent",
          borderRadius: 0,
          p: 0,
          boxShadow: "none",
        }}
      >
        <Box
          sx={{
            height: "100%",
            minHeight: 0,
            overflow: "hidden",
            p: { xs: 2, md: 3 },
            bgcolor: "neutral.50",
            borderRadius: "22px",
            "@media (max-height: 700px)": { p: 1.5 },
            "& .MuiOutlinedInput-root": { bgcolor: "common.white" },
          }}
        >
          <Stack spacing={2} sx={{ height: "100%", minHeight: 0 }}>
            {submitError ? (
              <Alert severity="error" onClose={() => setSubmitError("")}>
                {submitError}
              </Alert>
            ) : null}
            {sharedConsumers.length ? (
              <Alert severity="warning">
                Esta receta es compartida por {sharedConsumers.length} {sharedConsumers.length === 1 ? "producto" : "productos"}: {sharedConsumers.map((product) => product.name).join(", ")}. Los cambios afectarán su composición y costo.
              </Alert>
            ) : null}

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: { xs: "1fr", md: "0.72fr 1.28fr" },
                gap: { xs: 3, md: 4 },
                alignItems: "stretch",
                flex: 1,
                minHeight: 0,
                overflowY: "auto",
                overflowX: "hidden",
                pr: { xs: 0.5, md: 0.75 },
                scrollbarGutter: "stable",
              }}
            >
              <Stack spacing={2.4}>
                <SectionTitle>Información General</SectionTitle>

                <TextField
                  label="Nombre"
                  value={name}
                  onChange={(event) => {
                    setName(event.target.value);
                    setErrors((current) => ({ ...current, name: "" }));
                  }}
                  disabled={saving}
                  error={Boolean(errors.name)}
                  helperText={errors.name || `${name.length}/200`}
                  inputProps={{ maxLength: 200 }}
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />

                <Box>
                  <Typography sx={{ mb: 0.8, fontSize: 12, fontWeight: 800, color: "neutral.700" }}>
                    Sector
                  </Typography>
                  <ToggleButtonGroup
                    value={sector}
                    disabled={saving}
                    onChange={(_, value) => {
                      setSector(normalizeRecipeSectors(value));
                      setErrors((current) => ({ ...current, sector: "" }));
                    }}
                    aria-label="Sectores de la receta"
                    sx={{
                      gap: 1,
                      "& .MuiToggleButtonGroup-grouped": {
                        minWidth: 108,
                        border: "1px solid rgba(64,64,94,.18) !important",
                        borderRadius: "10px !important",
                        py: 0.8,
                        color: "primary.900",
                        fontSize: 12,
                        fontWeight: 800,
                        textTransform: "none",
                        "&.Mui-selected": {
                          bgcolor: "primary.500",
                          color: "logoblanco",
                          "&:hover": { bgcolor: "primary.600" },
                        },
                      },
                    }}
                  >
                    {RECIPE_SECTORS.map((value) => (
                      <ToggleButton key={value} value={value}>
                        {value}
                      </ToggleButton>
                    ))}
                  </ToggleButtonGroup>
                  {errors.sector ? (
                    <Typography sx={{ mt: 0.6, fontSize: 11, color: "error.main" }}>
                      {errors.sector}
                    </Typography>
                  ) : (
                    <Typography sx={{ mt: 0.6, fontSize: 11, color: "neutral.600" }}>
                      Podés seleccionar uno o ambos sectores
                    </Typography>
                  )}
                </Box>

                <TextField
                  label="Cantidad de porciones"
                  value={portionQuantity}
                  onChange={(event) => {
                    setPortionQuantity(
                      normalizeRecipeQuantityInput(event.target.value)
                    );
                    setErrors((current) => ({ ...current, portionQuantity: "" }));
                  }}
                  onBlur={() =>
                    setPortionQuantity((value) => formatRecipeQuantityInput(value))
                  }
                  disabled={saving}
                  error={Boolean(errors.portionQuantity)}
                  helperText={
                    errors.portionQuantity || "Cantidad que rinde esta receta"
                  }
                  inputProps={{ inputMode: "decimal", maxLength: 14 }}
                  fullWidth
                  size="small"
                  sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                />

                {showStatus ? (
                  <Box>
                    <Typography sx={{ mb: 0.8, fontSize: 12, fontWeight: 800, color: "neutral.700" }}>
                      Estado
                    </Typography>
                    <ToggleButtonGroup
                      value={isActive ? "active" : "inactive"}
                      exclusive
                      disabled={saving}
                      onChange={(_, value) => {
                        if (!value) return;
                        setIsActive(value === "active");
                      }}
                      aria-label="Estado de la receta"
                      sx={{
                        gap: 1,
                        "& .MuiToggleButtonGroup-grouped": {
                          minWidth: 108,
                          border: "1px solid rgba(64,64,94,.18) !important",
                          borderRadius: "10px !important",
                          py: 0.8,
                          color: "primary.900",
                          fontSize: 12,
                          fontWeight: 800,
                          textTransform: "none",
                          "&.Mui-selected": {
                            bgcolor: "primary.500",
                            color: "logoblanco",
                            "&:hover": { bgcolor: "primary.600" },
                          },
                        },
                      }}
                    >
                      <ToggleButton value="active">Activo</ToggleButton>
                      <ToggleButton value="inactive">Inactivo</ToggleButton>
                    </ToggleButtonGroup>
                  </Box>
                ) : null}
              </Stack>

              <Stack
                spacing={1.2}
                sx={{
                  minWidth: 0,
                  pl: { xs: 0, md: 4 },
                  borderLeft: { xs: "none", md: "2px solid rgba(2, 8, 23, 0.35)" },
                }}
              >
                <SectionTitle helper="Agregá un ingrediente o elaborado">
                  Receta
                </SectionTitle>

                <RecipeComponentSearch
                  selected={components}
                  disabled={saving}
                  disabledKinds={[
                    ...(ingredients.length >= MAX_RECIPE_COMPONENTS_PER_TYPE
                      ? ["ingredient"]
                      : []),
                    ...(preparedProducts.length >= MAX_RECIPE_COMPONENTS_PER_TYPE
                      ? ["preparedProduct"]
                      : []),
                  ]}
                  onSelect={handleSelectComponent}
                />
                {errors.components ? (
                  <Typography sx={{ fontSize: 11, color: "error.main" }}>
                    {errors.components}
                  </Typography>
                ) : null}
                <Box
                  sx={{
                    maxHeight: { xs: 300, md: "clamp(110px, 19vh, 190px)" },
                    minHeight: 0,
                    flexShrink: 0,
                    display: "flex",
                    flexDirection: "column",
                  }}
                >
                  <Typography sx={{ mb: 0.3, fontSize: 12, fontWeight: 800 }}>
                    Lista de ingredientes
                  </Typography>
                  {ingredients.length >= MAX_RECIPE_COMPONENTS_PER_TYPE ? (
                    <Typography sx={{ mb: 0.5, fontSize: 11, fontWeight: 800, color: "primary.700" }}>
                      Máximo de {MAX_RECIPE_COMPONENTS_PER_TYPE} insumos alcanzado.
                    </Typography>
                  ) : null}
                  {preparedProducts.length >= MAX_RECIPE_COMPONENTS_PER_TYPE ? (
                    <Typography sx={{ mb: 0.5, fontSize: 11, fontWeight: 800, color: "primary.700" }}>
                      Máximo de {MAX_RECIPE_COMPONENTS_PER_TYPE} elaborados alcanzado.
                    </Typography>
                  ) : null}
                  <Box sx={{ minHeight: 0, flex: 1, overflowY: "auto", pr: 0.7 }}>
                    {components.length ? (
                      components.map((component) => (
                        <RecipeComponentRow
                          key={componentKey(component)}
                          component={component}
                          error={(errors.lines || {})[componentKey(component)]}
                          disabled={saving}
                          onChange={(changes) => handleChangeComponent(component, changes)}
                          onRemove={() => handleRemoveComponent(component)}
                        />
                      ))
                    ) : (
                      <Box
                        sx={{
                          px: 2,
                          py: 2.2,
                          borderRadius: "14px",
                          bgcolor: "secondary.50",
                          border: "1px dashed rgba(64,64,94,.2)",
                        }}
                      >
                        <Typography sx={{ fontSize: 12, color: "neutral.600" }}>
                          Buscá y seleccioná los componentes de la receta.
                        </Typography>
                      </Box>
                    )}
                  </Box>
                </Box>

                <Divider />

                <Box sx={{ display: "flex", flexDirection: "column" }}>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mb: 0.6 }}
                  >
                    <Box>
                      <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                        Preparación
                      </Typography>
                      <Typography sx={{ fontSize: 10.5, color: "neutral.600" }}>
                        La descripción y los pasos son opcionales.
                      </Typography>
                    </Box>
                    <Button
                      type="button"
                      size="small"
                      startIcon={<AddIcon />}
                      disabled={saving}
                      onClick={addRecipeStep}
                      sx={{ textTransform: "none", fontWeight: 800 }}
                    >
                      Agregar paso
                    </Button>
                  </Stack>
                  <TextField
                    label="Descripción (opcional)"
                    value={recipeDescription}
                    onChange={(event) => {
                      setRecipeDescription(event.target.value);
                      setErrors((current) => ({ ...current, notes: "" }));
                    }}
                    disabled={saving}
                    multiline
                    minRows={2}
                    maxRows={4}
                    size="small"
                    fullWidth
                    sx={{ "& .MuiOutlinedInput-root": { borderRadius: "10px" } }}
                  />
                  {recipeSteps.length ? (
                    <Stack spacing={0.8} sx={{ mt: 1 }}>
                      {recipeSteps.map((step, index) => (
                        <Stack
                          key={step.key}
                          direction="row"
                          spacing={0.5}
                          alignItems="center"
                        >
                          <TextField
                            label={`Paso ${index + 1}`}
                            value={step.text}
                            onChange={(event) =>
                              updateRecipeStep(index, event.target.value)
                            }
                            disabled={saving}
                            size="small"
                            fullWidth
                            sx={{
                              "& .MuiOutlinedInput-root": {
                                borderRadius: "10px",
                              },
                            }}
                          />
                          <IconButton
                            type="button"
                            size="small"
                            disabled={saving || index === 0}
                            aria-label={`Subir paso ${index + 1}`}
                            onClick={() => moveRecipeStep(index, -1)}
                          >
                            <KeyboardArrowUpIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            type="button"
                            size="small"
                            disabled={saving || index === recipeSteps.length - 1}
                            aria-label={`Bajar paso ${index + 1}`}
                            onClick={() => moveRecipeStep(index, 1)}
                          >
                            <KeyboardArrowDownIcon fontSize="small" />
                          </IconButton>
                          <IconButton
                            type="button"
                            size="small"
                            color="error"
                            disabled={saving}
                            aria-label={`Quitar paso ${index + 1}`}
                            onClick={() => removeRecipeStep(index)}
                          >
                            <DeleteOutlineIcon fontSize="small" />
                          </IconButton>
                        </Stack>
                      ))}
                    </Stack>
                  ) : null}
                  {errors.notes ? (
                    <Typography sx={{ mt: 0.5, fontSize: 11, color: "error.main" }}>
                      {errors.notes}
                    </Typography>
                  ) : null}
                </Box>
              </Stack>
            </Box>

            <Divider sx={{ flexShrink: 0 }} />

            <Stack
              direction="row"
              spacing={1.2}
              sx={{
                flexShrink: 0,
                position: "relative",
                zIndex: 2,
                justifyContent: "flex-end",
                bgcolor: "neutral.50",
              }}
            >
              <Button
                type="button"
                variant="outlined"
                disabled={saving}
                onClick={requestCancel}
                sx={{ minWidth: 110, borderRadius: "22px", textTransform: "none", fontWeight: 800 }}
              >
                Cancelar
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={saving}
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                sx={{ minWidth: 124, borderRadius: "22px", textTransform: "none", fontWeight: 800 }}
              >
                {saving ? "Guardando..." : "Guardar"}
              </Button>
            </Stack>
          </Stack>
        </Box>
      </Box>

      <Dialog
        open={cancelDialogOpen}
        onClose={keepEditing}
        aria-labelledby="cancel-recipe-dialog-title"
        PaperProps={{ sx: { borderRadius: "18px", maxWidth: 430 } }}
      >
        <DialogTitle id="cancel-recipe-dialog-title" sx={{ fontWeight: 900, color: "primary.900" }}>
          ¿Seguro que querés cancelar?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: 14, color: "neutral.700" }}>
            Los cambios realizados se perderán y no podrán recuperarse.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <Button
            type="button"
            onClick={keepEditing}
            sx={{ borderRadius: "20px", textTransform: "none", fontWeight: 800 }}
          >
            Seguir editando
          </Button>
          <Button
            type="button"
            variant="contained"
            color="error"
            onClick={discardChanges}
            sx={{ borderRadius: "20px", textTransform: "none", fontWeight: 800 }}
          >
            Descartar cambios
          </Button>
        </DialogActions>
      </Dialog>
    </Stack>
  );
}
